<script setup lang="ts">
import { ref, onBeforeMount, computed, onMounted, watch } from "vue";
import { useApiClient } from "@/api/client";
import router from "@/router/router";
import { useI18n } from "vue-i18n";
import { useBaseStore } from "@/stores/base";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import RuleSetVersionType = App.Enums.RuleSetVersionType;

const api = useApiClient("bookingExperts");

const isLoading = ref(true);

const model_uuid = ref(null);
const version_uuid = ref(null);

const { t } = useI18n();

const getRuleModel = async () => {
	isLoading.value = true;

	const response = await useApiClient("ruleEngine").showModelByName(
		"bex-loyalty-automation"
	);

	if (response.isOk()) {
		router.push({
			name: "pro_loyalty.scenarios.builder",
			params: {
				model_uuid: response.value.uuid,
				version_uuid: response.value.active_version_uuid,
			},
		});

		model_uuid.value = response.value.uuid;
		version_uuid.value = response.value.active_version_uuid;
	}

	isLoading.value = false;
};

const store = useBaseStore();

const ensureAdministrationsLoaded = async () => {
	if (store.bexAdministrations.length > 0) return;

	const response = await api.getAdministrations();
	if (response.isOk()) {
		store.bexAdministrations = response.value;
		if (!store.selectedBexAdministration && response.value.length > 0) {
			store.selectedBexAdministration = response.value[0];
		}
	}
};

onBeforeMount(() => getRuleModel());
onMounted(async () => await ensureAdministrationsLoaded());

const scenarios = ref<RuleSetVersionType[]>(["per_transaction", "per_day"]);
const tab = ref(0);

const ruleEngineStore = useRuleEnginedStore();

/**
 * Undo can restore an edit made on the other scenario. The store selects the
 * version type that changed, so follow it here — otherwise the undo looks like
 * it did nothing.
 */
watch(
	() => ruleEngineStore.ruleEngineState.selectedModelVersionType,
	(versionType) => {
		const index = scenarios.value.indexOf(versionType);

		if (index !== -1) tab.value = index;
	}
);

const icon = computed(() =>
	tab.value === 0
		? "fa-regular fa-calendar-circle-user"
		: "fa-regular fa-calendar-day"
);

const cardTitle = computed(() =>
	tab.value === 0
		? "When reservation is processed, check:"
		: "Every day, check:"
);
</script>

<template>
	<v-card class="mt-10">
		<v-tabs v-model="tab">
			<v-tab v-for="(scenario, index) in scenarios" :key="index" :value="index">
				{{ t(`rule_engine.versionTypes.${scenario}.label`) }}
			</v-tab>
		</v-tabs>

		<v-card>
			<div class="text-center">
				<div class="text-h6 font-weight-medium mt-8">
					<v-chip :prepend-icon="icon" label>
						<v-card-title>{{ cardTitle }}</v-card-title>
					</v-chip>
					<div class="grey-line" />
				</div>
			</div>

			<v-divider />
			<div class="pa-5">
				<router-view :scenario="scenarios[tab]" />
			</div>
		</v-card>
	</v-card>
</template>

<style scoped lang="scss">
.grey-line {
	margin: 0 auto 0 auto;
	width: 1px;
	height: 35px;
	background-color: #eeeef0;
	border-radius: 3px;
}
</style>
